import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface IAddBusinessToAggregatorRequest {
  aggregatorCode: string;
}

interface IAddBusinessToAggregatorResponse {
  status?: boolean;
  message?: string;
  data?: {
    id?: string;
    category?: string;
    type?: string;
    name?: string;
    email?: string;
    address?: string;
    city?: string;
    state?: string;
    aggregatorId?: string;
    aggregatorCode?: string;
    hasAccount?: boolean;
    creatorId?: string;
    creatorType?: string;
    createdAt?: string;
    updatedAt?: string;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IAddBusinessToAggregatorResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const addBusinessToAggregatorAsync = createAsyncThunk(
  'aggregator/add',
  async (
    values: IAddBusinessToAggregatorRequest,
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId = localStorage.getItem('businessId');

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.post<IAddBusinessToAggregatorResponse>(
        '/aggregator/join',
        values,
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IAddBusinessToAggregatorResponse>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const aggregatorInvitationSlice = createSlice({
  name: 'aggregator/addBusiness',
  initialState,
  reducers: {
    resetAddBusinessToAggregator(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addBusinessToAggregatorAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(addBusinessToAggregatorAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(addBusinessToAggregatorAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IAddBusinessToAggregatorResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetAddBusinessToAggregator } =
  aggregatorInvitationSlice.actions;

export default aggregatorInvitationSlice.reducer;
